<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="flex flex-col space-y-6">
        <Header :text="$t('Administrator User Management')" />

        <!-- Requirements Sync Section -->
        <v-card class="mb-6" outlined v-if="requirementsStatus.isEmpty">
          <v-card-title class="pb-2">
            <span class="title font-weight-bold">IEC Requirements Database</span>
          </v-card-title>

          <v-card-text>
            <v-alert type="warning" text class="mb-4">
              <strong>Database is empty!</strong> The IEC requirements table is empty. Click the
              sync button below to import requirements from the Standard API.
            </v-alert>

            <div class="text-caption text-medium-emphasis mb-3">
              <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
              Last synced: {{ formatSyncedAt(requirementsStatus.lastSyncedAt) }}
            </div>

            <div class="d-flex gap-3">
              <v-btn
                color="primary"
                @click="syncIecRequirements"
                :loading="syncRequirementsLoading"
              >
                <v-icon left>mdi-cloud-download</v-icon>
                Sync IEC Requirements
              </v-btn>

              <v-btn
                color="info"
                text
                @click="checkRequirementsStatus"
                :loading="syncRequirementsLoading"
              >
                <v-icon left>mdi-refresh</v-icon>
                Refresh Status
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- Requirements Status (when not empty) -->
        <v-card class="mb-6" outlined v-else>
          <v-card-title class="pb-2">
            <span class="title font-weight-bold">IEC Requirements Database</span>
            <v-spacer></v-spacer>
            <v-chip color="success">{{ requirementsStatus.count }} requirements</v-chip>
          </v-card-title>

          <v-card-text>
            <v-alert type="success" text class="mb-4">
              {{ requirementsStatus.message }}
            </v-alert>

            <div class="text-caption text-medium-emphasis mb-3">
              <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
              Last synced: {{ formatSyncedAt(requirementsStatus.lastSyncedAt) }}
            </div>

            <div class="d-flex gap-3">
              <v-btn
                color="warning"
                @click="clearAndResyncRequirements"
                :loading="syncRequirementsLoading"
              >
                <v-icon left>mdi-refresh-circle</v-icon>
                Update Requirements
              </v-btn>

              <v-btn color="error" text @click="clearRequirementsDialog = true">
                <v-icon left>mdi-delete</v-icon>
                Clear Database
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- AI Features Card -->
        <v-card class="mb-6" outlined>
          <v-card-title class="pb-2">
            <v-icon left color="#0097a7">mdi-robot-outline</v-icon>
            <span class="title font-weight-bold">AI Features</span>
          </v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="8">
                <p class="text-body-2 mb-1">
                  <strong>AI Standards Assistant (RAG Analyzer)</strong>
                </p>
                <p class="text-caption text--secondary mb-0">
                  Enable or disable the AI Standards Assistant for all users. When enabled, a button
                  will appear on the Dashboard allowing users to access the AI-powered chatbot
                  trained on cybersecurity standards (IEC 62443, CRA, etc.).
                </p>
                <p class="text-caption text--secondary mt-1">
                  Endpoint:
                  <code>{{ ragAnalyzerUrl }}</code>
                </p>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center justify-md-end">
                <v-switch
                  :input-value="$store.getters.ragAnalyzerEnabled"
                  :label="$store.getters.ragAnalyzerEnabled ? 'Enabled' : 'Disabled'"
                  color="#0097a7"
                  inset
                  hide-details
                  :loading="ragAnalyzerToggleLoading"
                  @change="toggleRagAnalyzer"
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6" outlined>
          <v-card-title class="pb-2">
            <span class="title font-weight-bold">User Management</span>
            <v-spacer></v-spacer>
            <div class="d-flex align-center">
              <v-select
                v-model="searchField"
                :items="searchFields"
                item-text="text"
                item-value="value"
                :label="$t('search-field')"
                hide-details
                outlined
                dense
                class="mr-4 search-field"
                style="max-width: 200px;"
              ></v-select>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                outlined
                dense
                class="search-input"
                style="max-width: 300px;"
                @keyup.enter="applySearch"
              ></v-text-field>
            </div>
            <v-btn color="primary" class="ml-4" @click="openAddUserDialog">
              <v-icon left>mdi-plus</v-icon>
              Add User
            </v-btn>
          </v-card-title>

          <!-- Add Admin Notice Banner -->
          <v-alert type="info" text dismissible class="mx-4 mt-2">
            <strong>Admin Notice:</strong> This is the only page you have access to as an admin
            user. You can manage users, assign roles, and update user details from this panel.
          </v-alert>

          <v-data-table
            :headers="headers"
            :items="users"
            :search="search"
            :custom-filter="customFilter"
            :loading="tableLoading"
            class="elevation-1 certifai-table"
            :items-per-page="10"
            :footer-props="{
              itemsPerPageOptions: [5, 10, 20, 50],
              showFirstLastPage: true,
              itemsPerPageText: 'Users per page',
            }"
          >
            <template v-slot:[`item.roles`]="{ item }">
              <v-chip
                v-for="role in getDisplayRoles(item.roles)"
                :key="role"
                small
                class="mr-1"
                :color="getRoleColor(role)"
              >
                {{ role }}
              </v-chip>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="g-action-btn g-action-edit"
                    v-bind="attrs"
                    v-on="on"
                    @click="openEditUserDialog(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Edit User</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="g-action-btn g-action-delete"
                    v-bind="attrs"
                    v-on="on"
                    @click="confirmDeleteUser(item)"
                    :disabled="item.username === currentUsername"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete User</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </v-container>

    <!-- Add User Dialog -->
    <v-dialog v-model="addUserDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline-section white--text">
          <span class="text-h5">Add New User</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addUserDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="addUserForm" v-model="addUserFormValid" lazy-validation>
            <v-text-field
              v-model="newUser.username"
              label="Username"
              required
              :rules="[rules.required, rules.usernameFormat]"
              outlined
              dense
            ></v-text-field>

            <v-text-field
              v-model="newUser.email"
              label="Email"
              required
              :rules="[rules.required, rules.email]"
              outlined
              dense
            ></v-text-field>

            <v-text-field
              v-model="newUser.firstName"
              label="First Name"
              required
              :rules="[rules.required]"
              outlined
              dense
            ></v-text-field>

            <v-text-field
              v-model="newUser.lastName"
              label="Last Name"
              required
              :rules="[rules.required]"
              outlined
              dense
            ></v-text-field>

            <v-text-field
              v-model="newUser.password"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPassword ? 'text' : 'password'"
              :label="$t('password.title')"
              required
              :rules="[rules.required, rules.passwordStrength]"
              outlined
              dense
              @click:append="showPassword = !showPassword"
            ></v-text-field>

            <v-text-field
              v-model="newUser.confirmPassword"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPassword ? 'text' : 'password'"
              :label="$t('password.confirm')"
              required
              :rules="[rules.required, passwordMatchRule]"
              outlined
              dense
              @click:append="showPassword = !showPassword"
            ></v-text-field>

            <v-card outlined class="mt-4 pa-3">
              <div class="subtitle-1 mb-2">Assign Roles</div>

              <v-checkbox
                :input-value="newUser.roles.includes('certifai-user')"
                @change="selectAddRole('certifai-user')"
                label="User"
                hide-details
                class="my-0 py-1"
              ></v-checkbox>

              <v-checkbox
                :input-value="newUser.roles.includes('reviewer')"
                @change="selectAddRole('reviewer')"
                label="Reviewer"
                hide-details
                class="my-0 py-1"
              ></v-checkbox>

              <v-checkbox
                :input-value="newUser.roles.includes('auditor')"
                @change="selectAddRole('auditor')"
                label="Auditor"
                hide-details
                class="my-0 py-1"
              ></v-checkbox>

              <v-checkbox
                :input-value="newUser.roles.includes('admin-manager')"
                @change="selectAddRole('admin-manager')"
                label="Admin"
                hide-details
                class="my-0 py-1"
              ></v-checkbox>
            </v-card>
          </v-form>
        </v-card-text>

        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="addUserDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="addUser"
            :disabled="!addUserFormValid || addUserLoading"
            :loading="addUserLoading"
          >
            Add User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit User Dialog -->
    <v-dialog v-model="editUserDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline-section white--text">
          <span class="text-h5">Edit User</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editUserDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="editUserForm" v-model="editUserFormValid" lazy-validation>
            <v-text-field
              v-model="editedUser.username"
              label="Username"
              readonly
              outlined
              dense
            ></v-text-field>

            <v-text-field
              v-model="editedUser.email"
              label="Email"
              required
              :rules="[rules.required, rules.email]"
              outlined
              dense
            ></v-text-field>

            <v-text-field
              v-model="editedUser.firstName"
              label="First Name"
              required
              :rules="[rules.required]"
              outlined
              dense
            ></v-text-field>

            <v-text-field
              v-model="editedUser.lastName"
              label="Last Name"
              required
              :rules="[rules.required]"
              outlined
              dense
            ></v-text-field>

            <v-card outlined class="mt-4 pa-3">
              <div class="subtitle-1 mb-2">Manage Roles</div>

              <v-checkbox
                :input-value="editedUser.roles.includes('certifai-user')"
                @change="selectEditRole('certifai-user')"
                label="User"
                hide-details
                class="my-0 py-1"
              ></v-checkbox>

              <v-checkbox
                :input-value="editedUser.roles.includes('reviewer')"
                @change="selectEditRole('reviewer')"
                label="Reviewer"
                hide-details
                class="my-0 py-1"
              ></v-checkbox>

              <v-checkbox
                :input-value="editedUser.roles.includes('auditor')"
                @change="selectEditRole('auditor')"
                label="Auditor"
                hide-details
                class="my-0 py-1"
              ></v-checkbox>

              <v-checkbox
                :input-value="editedUser.roles.includes('admin-manager')"
                @change="selectEditRole('admin-manager')"
                label="Admin"
                hide-details
                class="my-0 py-1"
                :disabled="editedUser.username === currentUsername"
              ></v-checkbox>
            </v-card>

            <v-expansion-panels class="mt-4">
              <v-expansion-panel>
                <v-expansion-panel-header>Reset Password</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field
                    v-model="editedUser.password"
                    :append-icon="showEditPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showEditPassword ? 'text' : 'password'"
                    :label="$t('password.title')"
                    :rules="editedUser.password ? [rules.passwordStrength] : []"
                    outlined
                    dense
                    @click:append="showEditPassword = !showEditPassword"
                  ></v-text-field>

                  <v-text-field
                    v-model="editedUser.confirmPassword"
                    :append-icon="showEditPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showEditPassword ? 'text' : 'password'"
                    :label="$t('password.confirm')"
                    :rules="editedUser.password ? [rules.required, editPasswordMatchRule] : []"
                    outlined
                    dense
                    @click:append="showEditPassword = !showEditPassword"
                  ></v-text-field>

                  <v-alert type="info" text dense>
                    Leave empty to keep current password
                  </v-alert>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-form>
        </v-card-text>

        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="editUserDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="updateUser"
            :disabled="!editUserFormValid || updateUserLoading"
            :loading="updateUserLoading"
          >
            Update User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteUserDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline error white--text">
          Confirm Deletion
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete user
          <strong>{{ userToDelete ? userToDelete.username : '' }}</strong
          >? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="deleteUserDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" @click="deleteUser" :loading="deleteUserLoading">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Clear Requirements Confirmation Dialog -->
    <v-dialog v-model="clearRequirementsDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline error white--text">
          Confirm Clear
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete all <strong>{{ requirementsStatus.count }}</strong> IEC
          requirements from the database? This action cannot be undone. You will need to re-sync the
          requirements afterward.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="clearRequirementsDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" @click="clearIecRequirements" :loading="syncRequirementsLoading">
            Clear Database
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import Header from '@/components/helpers/Header';
import Loader from '@/components/helpers/Loader.vue';
import EventBus from '@/helpers/event-bus.js';
import UserService from '@/modules/admin/api/UserServiceApi';
import StandardApiAdmin from '@/modules/admin/api/StandardApiAdmin';
import PlatformConfigApi from '@/modules/admin/api/platformConfigApi';
import util from '@/mixins/util';
import dayjs from 'dayjs';

export default {
  name: 'AdminPanel',
  components: {
    Header,
    Loader,
  },
  mixins: [util],
  data() {
    return {
      ragAnalyzerUrl:
        process.env.VUE_APP_RAG_ANALYZER_URL || 'https://chatbot.certifai.digital.tecnalia.dev',
      loading: false,
      tableLoading: false,
      search: '',
      searchField: 'all',
      users: [],
      currentUsername: '',

      headers: [
        { text: 'Username', value: 'username', width: '15%' },
        { text: 'Full Name', value: 'fullName', width: '20%' },
        { text: 'Email', value: 'email', width: '25%' },
        { text: 'Roles', value: 'roles', width: '25%' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '15%' },
      ],

      // Add User Dialog
      addUserDialog: false,
      addUserFormValid: false,
      addUserLoading: false,
      showPassword: false,
      newUser: {
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        roles: ['certifai-user'],
      },

      // Edit User Dialog
      editUserDialog: false,
      editUserFormValid: false,
      updateUserLoading: false,
      showEditPassword: false,
      editedUser: {
        id: null,
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        roles: [],
      },

      // Delete User Dialog
      deleteUserDialog: false,
      deleteUserLoading: false,
      userToDelete: null,

      // Clear Requirements Dialog
      clearRequirementsDialog: false,

      // RAG Analyzer toggle
      ragAnalyzerToggleLoading: false,

      // Requirements Sync
      syncRequirementsLoading: false,
      requirementsStatus: {
        isEmpty: true,
        count: 0,
        message: '',
        lastSyncedAt: null,
      },
      syncDialogOpen: false,

      // Validation rules
      rules: {
        required: v => !!v || this.$t('password.required'),
        email: v => /.+@.+\..+/.test(v) || 'Please enter a valid email',
        usernameFormat: v =>
          /^[a-z0-9_-]+$/i.test(v) ||
          'Username can only contain letters, numbers, underscores and dashes',
        passwordStrength: v => v.length >= 8 || this.$t('password.rule-message'),
      },
    };
  },
  computed: {
    passwordMatchRule() {
      return () =>
        this.newUser.password === this.newUser.confirmPassword || this.$t('password.matching-rule');
    },
    editPasswordMatchRule() {
      return () =>
        this.editedUser.password === this.editedUser.confirmPassword ||
        this.$t('password.matching-rule');
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: 'Username', value: 'username' },
        { text: 'Full Name', value: 'fullName' },
        { text: 'Email', value: 'email' },
        { text: 'Roles', value: 'roles' },
      ];
    },
  },
  created() {
    this.loading = true;
    this.fetchUsers();
    this.getCurrentUser();
    this.checkRequirementsStatus();
  },
  methods: {
    selectAddRole(role) {
      this.newUser.roles = [role];
    },

    selectEditRole(role) {
      this.editedUser.roles = [role];
    },
    // Filter out default roles from UI display
    getDisplayRoles(roles) {
      if (!roles || !Array.isArray(roles)) return [];

      // Filter out the default Keycloak role
      return roles.filter(role => !role.toLowerCase().startsWith('default-roles-'));
    },

    async fetchUsers() {
      try {
        this.tableLoading = true;
        const response = await UserService.getUsers();

        // Map the data to match the expected format
        this.users = response.data
          .map(user => ({
            id: user.id,
            username: user.username,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username,
            roles: user.roles || [],
            enabled: user.enabled,
          }))
          .filter(user => !user.roles.includes('admin-manager'));

        console.log('Users loaded:', this.users);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to load users',
          color: 'error',
        });
      } finally {
        this.tableLoading = false;
        this.loading = false;
      }
    },

    async getCurrentUser() {
      try {
        const response = await UserService.getCurrentUser();
        this.currentUsername = response.data.username;
      } catch (error) {
        console.error('Failed to get current user:', error);
      }
    },

    getRoleColor(role) {
      switch (role.toLowerCase()) {
        case 'admin-manager':
          return 'red';
        case 'reviewer':
          return 'green';
        case 'auditor':
          return 'orange';
        case 'certifai-user':
        case 'user':
          return 'blue';
        default:
          return 'grey';
      }
    },

    openAddUserDialog() {
      this.newUser = {
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        roles: ['certifai-user'],
      };
      this.addUserDialog = true;
      this.$nextTick(() => {
        if (this.$refs.addUserForm) {
          this.$refs.addUserForm.resetValidation();
        }
      });
    },

    async addUser() {
      if (!this.$refs.addUserForm.validate()) return;

      try {
        this.addUserLoading = true;
        const invitationCode = await this.axios.get('/api/v1/admin/invitation-code');

        const userPayload = {
          username: this.newUser.username,
          email: this.newUser.email,
          password: this.newUser.password,
          confirmPassword: this.newUser.password, // Backend expects confirmPassword field
          firstName: this.newUser.firstName,
          lastName: this.newUser.lastName,
          invitationCode: invitationCode.data,
          role: this.newUser.roles[0],
        };

        await UserService.createUser(userPayload);

        EventBus.$emit('snackbar', {
          text: `User ${this.newUser.username} created successfully`,
          color: 'success',
        });

        this.addUserDialog = false;
        this.fetchUsers();
      } catch (error) {
        console.error('Failed to create user:', error);

        // Show more detailed error message
        let errorMessage = 'Unknown error';
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }

        EventBus.$emit('snackbar', {
          text: `Failed to create user: ${errorMessage}`,
          color: 'error',
        });
      } finally {
        this.addUserLoading = false;
      }
    },

    openEditUserDialog(user) {
      this.userToEdit = user;
      this.editedUser = {
        id: user.id,
        username: user.username,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        password: '',
        confirmPassword: '',
        roles: this.getDisplayRoles(user.roles),
      };

      this.editUserDialog = true;
      this.$nextTick(() => {
        if (this.$refs.editUserForm) {
          this.$refs.editUserForm.resetValidation();
        }
      });
    },

    async updateUser() {
      if (!this.$refs.editUserForm.validate()) return;

      try {
        this.updateUserLoading = true;

        // Build update data with all fields
        const updateData = {
          id: this.editedUser.id,
          username: this.editedUser.username,
          email: this.editedUser.email,
          firstName: this.editedUser.firstName,
          lastName: this.editedUser.lastName,
          enabled: true, // Assuming enabled by default
          roles: [...this.editedUser.roles],
          password: null, // Initialize as null
        };

        // Only include password if it was changed
        if (this.editedUser.password && this.editedUser.password.trim()) {
          updateData.password = this.editedUser.password;
        }

        // Make sure we preserve default roles
        if (this.userToEdit && this.userToEdit.roles) {
          // Find any default roles in the original user
          const defaultRoles = this.userToEdit.roles.filter(role =>
            role.toLowerCase().startsWith('default-roles-'),
          );

          // Add them back to the roles being sent
          if (defaultRoles.length > 0) {
            updateData.roles = [...updateData.roles, ...defaultRoles];
          }
        }

        await UserService.updateUser(this.editedUser.id, updateData);

        EventBus.$emit('snackbar', {
          text: `User ${this.editedUser.username} updated successfully${
            updateData.password ? ' (password changed)' : ''
          }`,
          color: 'success',
        });

        this.editUserDialog = false;
        this.fetchUsers();
      } catch (error) {
        console.error('Failed to update user:', error);
        EventBus.$emit('snackbar', {
          text: `Failed to update user: ${error.response?.data?.message || 'Unknown error'}`,
          color: 'error',
        });
      } finally {
        this.updateUserLoading = false;
      }
    },

    confirmDeleteUser(user) {
      this.userToDelete = user;
      this.deleteUserDialog = true;
    },

    async deleteUser() {
      if (!this.userToDelete) return;

      try {
        this.deleteUserLoading = true;
        await UserService.deleteUser(this.userToDelete.id);

        EventBus.$emit('snackbar', {
          text: `User ${this.userToDelete.username} deleted successfully`,
          color: 'success',
        });

        this.deleteUserDialog = false;
        this.fetchUsers();
      } catch (error) {
        console.error('Failed to delete user:', error);
        EventBus.$emit('snackbar', {
          text: `Failed to delete user: ${error.response?.data?.message || 'Unknown error'}`,
          color: 'error',
        });
      } finally {
        this.deleteUserLoading = false;
      }
    },

    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip non-searchable fields and non-string values
          if (key === 'actions' || key === 'id' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            // Special handling for roles array
            if (key === 'roles' && Array.isArray(item[key])) {
              return item[key].some(role =>
                role
                  .toString()
                  .toLowerCase()
                  .includes(search),
              );
            }

            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            // Special handling for roles array
            if (this.searchField === 'roles' && Array.isArray(item[this.searchField])) {
              return item[this.searchField].some(role =>
                role
                  .toString()
                  .toLowerCase()
                  .includes(search),
              );
            }

            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },

    applySearch() {
      // This method can be used for any additional search logic if needed
      // For now it's a placeholder as the filtering happens automatically
    },

    formatSyncedAt(value) {
      if (!value) return 'Never';
      return dayjs(value).format('YYYY-MM-DD HH:mm');
    },

    async checkRequirementsStatus() {
      try {
        const response = await StandardApiAdmin.checkRequirementsStatus();
        this.requirementsStatus = response.data;

        console.log('Requirements status:', this.requirementsStatus);

        // If empty, automatically show sync dialog
        if (this.requirementsStatus.isEmpty) {
          this.$nextTick(() => {
            this.syncDialogOpen = true;
          });
        }
      } catch (error) {
        console.error('Failed to check requirements status:', error);
        this.requirementsStatus = {
          isEmpty: true,
          count: 0,
          message: 'Error checking status',
        };
      }
    },

    async syncIecRequirements() {
      this.syncRequirementsLoading = true;
      try {
        const response = await StandardApiAdmin.syncRequirements();

        EventBus.$emit('snackbar', {
          text: `Successfully synced ${response.data.count} IEC requirements!`,
          color: 'success',
        });

        // Refresh status
        this.checkRequirementsStatus();
        this.syncDialogOpen = false;
      } catch (error) {
        console.error('Failed to sync requirements:', error);
        EventBus.$emit('snackbar', {
          text: error.response?.data?.message || 'Failed to sync requirements',
          color: 'error',
        });
      } finally {
        this.syncRequirementsLoading = false;
      }
    },

    async clearAndResyncRequirements() {
      try {
        // First clear
        await StandardApiAdmin.clearRequirements();

        EventBus.$emit('snackbar', {
          text: 'Clearing old requirements...',
          color: 'info',
        });

        // Then sync new
        setTimeout(() => {
          this.syncIecRequirements();
        }, 500);
      } catch (error) {
        console.error('Failed to clear requirements:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to clear requirements',
          color: 'error',
        });
      }
    },

    async clearIecRequirements() {
      this.syncRequirementsLoading = true;
      try {
        const response = await StandardApiAdmin.clearRequirements();

        EventBus.$emit('snackbar', {
          text: `Successfully cleared ${response.data.deletedCount} requirements!`,
          color: 'success',
        });

        // Refresh status
        this.checkRequirementsStatus();
        this.clearRequirementsDialog = false;
      } catch (error) {
        console.error('Failed to clear requirements:', error);
        EventBus.$emit('snackbar', {
          text: error.response?.data?.message || 'Failed to clear requirements',
          color: 'error',
        });
      } finally {
        this.syncRequirementsLoading = false;
      }
    },

    async toggleRagAnalyzer(val) {
      this.ragAnalyzerToggleLoading = true;
      try {
        const response = await PlatformConfigApi.setRagAnalyzerEnabled(!!val);
        this.$store.commit('setRagAnalyzerEnabled', response.data.ragAnalyzerEnabled);
        EventBus.$emit('snackbar', {
          text: `AI Standards Assistant ${
            response.data.ragAnalyzerEnabled ? 'enabled' : 'disabled'
          } for all users`,
          color: 'success',
        });
      } catch (error) {
        console.error('Failed to toggle RAG Analyzer:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to update AI Standards Assistant setting',
          color: 'error',
        });
      } finally {
        this.ragAnalyzerToggleLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.headline-section {
  background: linear-gradient(135deg, #581e5c 0%, #734b6d 100%);
  width: 100%;
  margin: 0;
  padding: 16px;
}

.v-data-table ::v-deep th {
  font-weight: bold;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.search-field {
  min-width: 150px;
}

.search-input {
  min-width: 250px;
}
</style>
